import * as React from 'react'

import { Button } from '../../../../main/components/button'
import { SpecificChartFunctionality, TIMEFRAME } from '../../../../main/constants'
import { Instrument } from '../../../../main/types'
import { decodeQueryString, encodeQueryString } from '../../../queryString'
import { FuturesForexCryptoTimeframeBar } from '../../../shared/components/FuturesForexCryptoTimeframeBar'
import { getCurrencyLongName } from '../../currencyUtils'
import { PageType, TimeFrames } from '../../types'

interface MenuProps<Key> {
  isDetailOpen?: boolean
  activePage: PageType
  timeframes: TimeFrames<Key>
  currencies?: {
    currencies: string[]
    active: string
    link: (currency: string) => string
  }
  company?: string
  onTimeframeChange?: (timeframe: TIMEFRAME) => void
  onDateRangeChange?: (dateRange: string | undefined) => void
}

export default function Menu<Key extends string>({
  isDetailOpen,
  activePage,
  timeframes,
  currencies,
  company,
  onTimeframeChange,
  onDateRangeChange,
}: MenuProps<Key>) {
  const params = decodeQueryString()
  const queryParams = (Object.keys(params).length > 0 ? '?' : '') + encodeQueryString({ ...params, t: undefined })
  return (
    <div className="flex h-12 items-center justify-between py-2.5">
      <div className="flex space-x-px font-sans">
        <Button
          as="a"
          href={`/crypto.ashx${queryParams}`}
          active={activePage === PageType.Quotes}
          theme="chipTransparent"
        >
          Quotes
        </Button>
        <Button
          as="a"
          href={`/crypto_performance.ashx${queryParams}`}
          theme="chipTransparent"
          active={activePage === PageType.Performance}
        >
          Performance
        </Button>
        <Button
          as="a"
          href={`/crypto_charts.ashx${queryParams}`}
          theme="chipTransparent"
          active={activePage === PageType.Charts}
        >
          Charts
        </Button>
      </div>
      {currencies && (
        <div className="flex w-64 justify-end space-x-px font-sans xl:w-96">
          {currencies.currencies.map((currency) => (
            <Button
              key={currency}
              as="a"
              href={currencies.link(currency)}
              theme="chipTransparent"
              padding="small"
              active={currency === currencies.active}
              title={currency}
            >
              {currency}
            </Button>
          ))}
        </div>
      )}
      <div className="flex w-64 justify-end space-x-px font-sans xl:w-96">
        {activePage === PageType.Charts ? (
          <FuturesForexCryptoTimeframeBar
            isDetail={isDetailOpen}
            title="Crypto"
            group={company ? `${company?.split('/').map(getCurrencyLongName).join(' / ')} ${company}` : undefined}
            onTimeframeChange={onTimeframeChange}
            onDateRangeChange={onDateRangeChange}
            specificChartFunctionality={SpecificChartFunctionality.cryptoPage}
            instrument={Instrument.Crypto}
          />
        ) : (
          timeframes.timeframes.map((timeframe) => (
            <Button
              key={timeframe.key}
              as="a"
              {...{ href: timeframes.link?.(timeframe.key as Key) }}
              theme="chipTransparent"
              padding="small"
              active={timeframes.active === timeframe.key}
              onClick={(ev: React.MouseEvent<HTMLAnchorElement>) => timeframes.onClick?.(timeframe.key as Key, ev)}
              title={timeframe.title}
            >
              {timeframe.value}
            </Button>
          ))
        )}
      </div>
    </div>
  )
}
